import { useState } from "react";
import { useCategories } from "@/hooks/useManageServices";
import { useConfirmUnsaved } from "@/hooks/useUnsavedChanges";
import { ManageServicesList } from "./ManageServicesList";
import { AddEditServiceForm } from "./AddEditServiceForm";
import { Loader2, Layers } from "lucide-react";
import { ServiceDetailView } from "./ServiceDetailView";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { useAuthStore } from "@/stores/authStore";

type ManageServicesView = "list" | "add" | "edit" | "view";

export const ManageServicesHome = () => {
  const [view, setView] = useState<ManageServicesView>("list");
  const [editingServiceId, setEditingServiceId] = useState<string>("");
  const confirmUnsaved = useConfirmUnsaved();
  const { hasPermission } = useAuthStore();
  const canManageServices = hasPermission(CONSOLE_PERMISSIONS.MANAGE_SERVICES);

  const { data: categories, isLoading, isError } = useCategories();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-muted-foreground gap-2">
        <Layers className="h-10 w-10 mb-2 opacity-40" />
        <p className="text-sm font-medium">Failed to load categories.</p>
        <p className="text-xs">Please try refreshing the page.</p>
      </div>
    );
  }

  const handleAddNew = () => {
    if (!canManageServices) return;
    confirmUnsaved(() => {
      setEditingServiceId("");
      setView("add");
    });
  };

  const handleEdit = (serviceId: string) => {
    if (!canManageServices) return;
    confirmUnsaved(() => {
      setEditingServiceId(serviceId);
      setView("edit");
    });
  };

  const handleView = (serviceId: string) => {
    confirmUnsaved(() => {
      setEditingServiceId(serviceId);
      setView("view");
    });
  };

  const handleBack = () => {
    confirmUnsaved(() => {
      setEditingServiceId("");
      setView("list");
    });
  };

  if (view === "view") {
    return (
      <ServiceDetailView
        serviceId={editingServiceId}
        onEdit={handleEdit}
        onBack={handleBack}
        canManageServices={canManageServices}
      />
    );
  }

  if ((view === "add" || view === "edit") && canManageServices) {
    return (
      <AddEditServiceForm
        serviceId={editingServiceId}
        categories={categories ?? []}
        onBack={
          view === "edit" ? () => handleView(editingServiceId) : handleBack
        }
      />
    );
  }

  return (
    <ManageServicesList
      categories={categories ?? []}
      onAddNew={handleAddNew}
      onView={handleView}
      canManageServices={canManageServices}
    />
  );
};
